import React, { useMemo } from "react";
import { connect } from "react-redux";

import { Droppable } from "react-beautiful-dnd";
import { defineMessages, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SchemaFieldType from "@mapmycustomers/shared/enum/SchemaFieldType";
import {
  EntityTypesSupportingFieldCustomization,
  entityTypesSupportingGroups,
  EntityTypeSupportingGroups,
} from "@mapmycustomers/shared/types/entity";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";

import { getActiveEntityType } from "@app/scene/settings/component/FormLayouts/store";
import { getFields } from "@app/scene/settings/component/ManageFields/store";
import { RootState } from "@app/store/rootReducer";
import displayNameComparator from "@app/util/comparator/displayNameComparator";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import CategorizedField from "../../type/CategorizedField";
import SchemaMap from "../../type/SchemaMap";
import PaletteCategoryList from "../PaletteCategoryList";

import styles from "./Palette.module.scss";

interface Props {
  entityType: EntityTypesSupportingFieldCustomization;
  fields: SchemaField[];
  filter?: string;
  schemaMap: SchemaMap;
}

const messages = defineMessages({
  custom: {
    id: "settings.formLayouts.palette.custom",
    defaultMessage: "Custom Fields",
    description: "Custom Fields section title",
  },
  divider: {
    id: "settings.formLayouts.palette.divider",
    defaultMessage: "Divider",
    description: "Divider title",
  },
  groups: {
    id: "settings.formLayouts.palette.groups",
    defaultMessage: "Groups",
    description: "Groups field name for fields palette",
  },
  layout: {
    id: "settings.formLayouts.palette.layout",
    defaultMessage: "Layout",
    description: "Layout section title",
  },
  note: {
    id: "settings.formLayouts.palette.note",
    defaultMessage: "Note",
    description: "Note field name for fields palette",
  },
  relationships: {
    id: "settings.formLayouts.palette.relationships",
    defaultMessage: "Relationships",
    description: "Relationships section title",
  },
  route: {
    id: "settings.formLayouts.palette.route",
    defaultMessage: "Route",
    description: "Route field name for fields palette",
  },
  systemFields: {
    id: "settings.formLayouts.palette.systemFields",
    defaultMessage: "System Fields",
    description: "System Fields section title",
  },
});

const Palette: React.FC<Props> = ({ entityType, fields, filter, schemaMap }) => {
  const intl = useIntl();
  const fieldModel = getFieldModelByEntityType(entityType);

  const categorizedFields: CategorizedField[] = useMemo(() => {
    const preparedFilter =
      typeof filter === "string" && filter ? filter.trim().toLowerCase() : undefined;

    const layoutFields = {
      fields: [
        {
          disabled: false,
          displayName: intl.formatMessage(messages.divider),
          field: "divider",
          fieldType: "divider",
          required: false,
        },
      ]
        .filter((schemaField) =>
          preparedFilter ? schemaField.displayName.toLowerCase().includes(preparedFilter) : true
        )
        .sort(displayNameComparator),
      name: intl.formatMessage(messages.layout),
    };

    const systemFields = {
      fields: fields
        .filter(
          (schemaField) =>
            !["createdAt", "importId", "reliability", "updatedAt"].includes(schemaField.field)
        )
        .filter((schemaField) => {
          const field = fieldModel.getByPlatformName(schemaField.field);
          return (
            (schemaField.fieldType === SchemaFieldType.STANDARD ||
              schemaField.fieldType === SchemaFieldType.FILE) &&
            field &&
            !field.hasAnyFeature(
              FieldFeature.GROUP_FIELD,
              FieldFeature.RELATIONSHIPS,
              FieldFeature.ROUTE_FIELD
            )
          );
        })
        .map((schemaField) => {
          const displayName = fieldModel.getByPlatformName(
            schemaField.field
          )!.fieldManagementDisplayName;
          return {
            disabled: schemaMap.has(schemaField.field),
            displayName,
            field: schemaField.field,
            fieldType: schemaField.fieldType,
            required: false,
          };
        })
        .filter((field) =>
          preparedFilter ? field.displayName.toLowerCase().includes(preparedFilter) : true
        )
        .sort(displayNameComparator),
      name: intl.formatMessage(messages.systemFields),
    };

    const relationshipFields = {
      fields: [
        ...fields
          .filter((schemaField) => {
            const field = fieldModel.getByPlatformName(schemaField.field);
            return (
              field &&
              field.hasAnyFeature(
                FieldFeature.GROUP_FIELD,
                FieldFeature.RELATIONSHIPS,
                FieldFeature.ROUTE_FIELD
              )
            );
          })
          .map((schemaField) => ({
            disabled: schemaMap.has(schemaField.field),
            displayName: fieldModel.getByPlatformName(schemaField.field)!
              .fieldManagementDisplayName,
            field: schemaField.field,
            fieldType: schemaField.fieldType,
            required: false,
          }))
          .filter((field) =>
            preparedFilter ? field.displayName.toLowerCase().includes(preparedFilter) : true
          ),

        ...(entityTypesSupportingGroups.includes(entityType as EntityTypeSupportingGroups)
          ? [
              {
                disabled: schemaMap.has("groups"),
                displayName: intl.formatMessage(messages.groups),
                field: "groups",
                fieldType: "group",
                required: false,
              },
            ]
          : []),
      ]
        .filter((field) =>
          preparedFilter ? field.displayName.toLowerCase().includes(preparedFilter) : true
        )
        .sort(displayNameComparator),
      name: intl.formatMessage(messages.relationships),
    };

    const customFields = {
      fields: fields
        .filter(
          (schemaField) =>
            schemaField.fieldType === SchemaFieldType.CUSTOM &&
            fieldModel.getByPlatformName(schemaField.field)
        )
        .map((schemaField) => ({
          disabled: schemaMap.has(schemaField.field),
          displayName: fieldModel.getByPlatformName(schemaField.field)!.fieldManagementDisplayName,
          field: schemaField.field,
          fieldType: schemaField.fieldType,
          required: false,
        }))
        .filter((schemaField) =>
          preparedFilter ? schemaField.displayName.toLowerCase().includes(preparedFilter) : true
        )
        .sort(displayNameComparator),
      name: intl.formatMessage(messages.custom),
    };

    return [
      ...(customFields.fields.length ? [customFields] : []),
      layoutFields,
      ...(relationshipFields.fields.length ? [relationshipFields] : []),
      ...(systemFields.fields.length ? [systemFields] : []),
    ];
  }, [entityType, fieldModel, fields, filter, intl, schemaMap]);

  return (
    <Droppable direction="vertical" droppableId="palette" isDropDisabled>
      {(droppableProvided, droppableSnapshot) => {
        let draggableIndex = 0;
        return (
          <div
            {...droppableProvided.droppableProps}
            className={styles.container}
            ref={droppableProvided.innerRef}
            tabIndex={-1}
          >
            {categorizedFields.map((category, index) => {
              draggableIndex += category.fields?.length ?? 0;
              return (
                <PaletteCategoryList
                  category={category}
                  key={index}
                  locked={!!droppableSnapshot.draggingFromThisWith}
                  schemaMap={schemaMap}
                  startIndex={draggableIndex}
                />
              );
            })}
          </div>
        );
      }}
    </Droppable>
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getActiveEntityType(state),
  fields: getFields(state),
});

export default connect(mapStateToProps)(Palette);
